import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { contactFormSchema, type ContactFormInput } from "@dosa/validation";
import { Button, FieldLabel, FieldError, Input, Textarea, Alert } from "@dosa/ui";

export function ContactPage() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormInput>({ resolver: zodResolver(contactFormSchema) });

  const onSubmit = async () => {
    await new Promise((r) => setTimeout(r, 400));
    setSubmitted(true);
  };

  return (
    <div className="container-app max-w-xl py-16 lg:py-20">
      <h1 className="text-4xl font-semibold text-charcoal-900">{t("contact.title")}</h1>
      <p className="mt-3 text-lg text-charcoal-600">{t("contact.subtitle")}</p>

      {submitted ? (
        <Alert variant="success" className="mt-8">
          {t("contact.success")}
        </Alert>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-5" noValidate>
          <div>
            <FieldLabel htmlFor="fullName">{t("contact.fullName")}</FieldLabel>
            <Input id="fullName" aria-invalid={!!errors.fullName} {...register("fullName")} />
            <FieldError>{errors.fullName?.message}</FieldError>
          </div>
          <div>
            <FieldLabel htmlFor="email">{t("contact.email")}</FieldLabel>
            <Input id="email" type="email" aria-invalid={!!errors.email} {...register("email")} />
            <FieldError>{errors.email?.message}</FieldError>
          </div>
          <div>
            <FieldLabel htmlFor="phone">{t("contact.phone")}</FieldLabel>
            <Input id="phone" aria-invalid={!!errors.phone} {...register("phone")} />
            <FieldError>{errors.phone?.message}</FieldError>
          </div>
          <div>
            <FieldLabel htmlFor="subject">{t("contact.subject")}</FieldLabel>
            <Input id="subject" aria-invalid={!!errors.subject} {...register("subject")} />
            <FieldError>{errors.subject?.message}</FieldError>
          </div>
          <div>
            <FieldLabel htmlFor="message">{t("contact.message")}</FieldLabel>
            <Textarea id="message" rows={5} aria-invalid={!!errors.message} {...register("message")} />
            <FieldError>{errors.message?.message}</FieldError>
          </div>
          <Button type="submit" isLoading={isSubmitting} size="lg">
            {t("contact.send")}
          </Button>
        </form>
      )}
    </div>
  );
}
